<script>
    import { onMount } from "svelte";
    import CursorTrail from "$lib/components/CursorTrail.svelte";
    import CelestialSky from "$lib/components/CelestialSky.svelte";

    onMount(() => {
        const svgWrap = document.querySelector("#mission .svg-wrap");
        if (!svgWrap) {
            return;
        }

        const baseIcon = svgWrap.querySelector("svg");
        if (!baseIcon) {
            return;
        }

        for (let i = 0; i < 10; i += 1) {
            svgWrap.append(baseIcon.cloneNode(true));
        }

        if ("IntersectionObserver" in window) {
            const animationRoot = document.querySelector("#mission .animation");
            if (animationRoot) {
                const observer = new IntersectionObserver(
                    (entries) => {
                        entries.forEach((entry) => {
                            const svgGroup =
                                entry.target.querySelector(".svg-wrap");
                            if (svgGroup) {
                                svgGroup.style.animationPlayState =
                                    entry.isIntersecting ? "running" : "paused";
                            }
                        });
                    },
                    { threshold: 0.15 },
                );
                observer.observe(animationRoot);
            }
        }
    });
</script>

<svelte:head>
    <title>dinglandia | Our Mission</title>
    <meta
        name="description"
        content="To provide fortune 500 quality design & development solutions to the masses using value based pricing to make it work for everyone."
    />

    <meta property="og:title" content="Our Mission" />
    <meta property="og:site_name" content="dinglandia" />
    <meta property="og:url" content="https://dinglandia.design/mission" />
    <meta
        property="og:description"
        content="To provide fortune 500 quality design & development solutions to the masses using value based pricing to make it work for everyone."
    />
    <meta property="og:type" content="business.business" />
    <meta
        property="og:image"
        content="https://dinglandia.design/og/mission.jpg"
    />
</svelte:head>

<!-- Intro -->
<section id="intro" class="intro" data-trail="wrapper">
    <div class="icon-new fadeInUp" name="logo"></div>
    <h1 class="flash">mission</h1>

    <!-- Cursor Trail Images -->
    <CursorTrail />
</section>
<!-- //Intro -->

<!-- Down Arrow -->
<a href="#mission" aria-label="Jump to mission content">
    <svg class="icon icon-arrow down-arrow"
        ><use xlink:href="#icon-arrow"></use></svg
    >
</a>
<!-- //Down Arrow -->

<!-- Mission -->
<section id="mission" class="mission">
    <div class="container">
        <div class="row">
            <div class="col-md-6 offset-md-3">
                <h2 class="text-center">Our Mission is Simple:</h2>
                <br />
                <div class="mission-copy">
                    <div class="lightning-wrap" aria-hidden="true">
                        <div class="animation">
                            <div class="wrapper">
                                <div class="svg-wrap">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="200"
                                        height="505"
                                        viewBox="0 0 200 505"
                                        fill="none"
                                    >
                                        <mask
                                            id="path-1-inside-1_56_1048"
                                            fill="white"
                                        >
                                            <path
                                                d="M107.861 218.449L199.031 242.547H200L69.7637 505L92.1387 286.551L0 262.453L130.236 0L107.861 218.449Z"
                                            />
                                        </mask>
                                        <g>
                                            <path
                                                d="M107.861 218.449L199.031 242.547H200L69.7637 505L92.1387 286.551L0 262.453L130.236 0L107.861 218.449Z"
                                                fill="#FFD700"
                                            />
                                            <path
                                                d="M107.861 218.449L104.877 218.144L104.616 220.694L107.095 221.35L107.861 218.449ZM199.031 242.547L198.265 245.447L198.641 245.547H199.031V242.547ZM200 242.547L202.687 243.88L204.838 239.547H200V242.547ZM69.7637 505L66.7793 504.694L72.451 506.334L69.7637 505ZM92.1387 286.551L95.1231 286.856L95.385 284.299L92.8978 283.648L92.1387 286.551ZM0 262.453L-2.68733 261.12L-4.32633 264.423L-0.759079 265.355L0 262.453ZM130.236 0L133.221 0.30568L127.549 -1.33352L130.236 0ZM107.861 218.449L107.095 221.35L198.265 245.447L199.031 242.547L199.798 239.646L108.628 215.549L107.861 218.449ZM199.031 242.547V245.547H200V242.547V239.547H199.031V242.547ZM200 242.547L197.313 241.213L67.0763 503.666L69.7637 505L72.451 506.334L202.687 243.88L200 242.547ZM69.7637 505L72.7481 505.306L95.1231 286.856L92.1387 286.551L89.1543 286.245L66.7793 504.694L69.7637 505ZM92.1387 286.551L92.8978 283.648L0.759079 259.551L0 262.453L-0.759079 265.355L91.3796 289.453L92.1387 286.551ZM0 262.453L2.68733 263.787L132.924 1.33352L130.236 0L127.549 -1.33352L-2.68733 261.12L0 262.453ZM130.236 0L127.252 -0.30568L104.877 218.144L107.861 218.449L110.846 218.755L133.221 0.30568L130.236 0Z"
                                                fill="#FFA500"
                                                mask="url(#path-1-inside-1_56_1048)"
                                            />
                                        </g>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="mission-text">
                        <p>
                            To provide Fortune 500 quality <span class="style"
                                >design and development solutions</span
                            >
                            to businesses of all sizes using
                            <span class="style">value-based pricing</span>
                            that makes premium work accessible to everyone. We believe
                            that exceptional digital experiences shouldn't be reserved
                            for
                            <span class="style">enterprise budgets</span> alone. Our
                            mission is to partner with ambitious teams to build digital
                            products that are not only beautiful, but strategically
                            aligned, performance-driven, and built to deliver measurable
                            growth.
                        </p>
                        <p>
                            <span class="style">Performance</span> and
                            <span class="style">optimization</span>
                            aren't just buzzwords for us—they're the foundation of
                            everything we create. Every millisecond matters. Every
                            interaction counts. We obsess over
                            <span class="style">load times</span>,
                            <span class="style">responsiveness</span>, and
                            seamless functionality because superior performance
                            directly translates to better user engagement,
                            higher conversion rates, and tangible business
                            results.
                        </p>
                        <p>
                            Through meticulous attention to <span class="style"
                                >UX (user experience)</span
                            >
                            and <span class="style">UI (user interface)</span> design,
                            we craft digital products that don't just look beautiful—they
                            feel intuitive, respond instantly, and guide users effortlessly
                            toward their goals. This is where artistry meets engineering,
                            where visual design harmonizes with technical excellence
                            to create experiences that users love and remember.
                        </p>
                    </div>
                </div>
                <div class="d-grid gap-2 col-6 mx-auto">
                    <a href="/work" class="btn btn-lg btn-custom" type="button"
                        >view work</a
                    >
                </div>
            </div>
        </div>
    </div>
</section>
<!-- //Mission -->

<!-- New Mountain Parallax -->
<main class="content-grid">
    <section class="breakout">
        <CelestialSky />
        <div
            class="frame frame-square parallax-y"
            role="img"
            aria-label="Multi-layered scene depicting an underwater scene of some fish and Manta Ray. Set within the outline of a Manta Ray."
            style="--multiplier: 4"
        >
            <img src="/img/mountains/mtnBlu5.svg" alt="" style="--offset:0.1" />
            <img
                src="/img/mountains/mtnBlu4.svg"
                alt=""
                style="--offset:0.075"
            />
            <img
                src="/img/mountains/mtnBlue3.svg"
                alt=""
                style="--offset:0.05"
            />
            <img
                src="/img/mountains/mtnBlu2_fix.svg"
                alt=""
                style="--offset:0.025"
            />
            <img src="/img/mountains/mtnBlue.svg" alt="" style="--offset:0.012" />
            <div class="mountain-base"></div>
        </div>
        <p class="supports"></p>
    </section>
</main>
<!-- .//New Mountain Parallax -->

<!-- Services -->
<section id="services" class="services">
    <div class="container">
        <h2 class="text-center">What We Do</h2>
        <div class="services-grid">
            <a href="/services#v-pills-discover">
                <svg class="icon icon-discover"
                    ><use xlink:href="#icon-discover"></use></svg
                >
                <p>Discover</p>
            </a>
            <a href="/services#v-pills-design">
                <svg class="icon icon-design"
                    ><use xlink:href="#icon-design"></use></svg
                >
                <p>Design</p>
            </a>
            <a href="/services#v-pills-develop">
                <svg class="icon icon-develop"
                    ><use xlink:href="#icon-develop"></use></svg
                >
                <p>Develop</p>
            </a>
            <a href="/services#v-pills-devops">
                <svg class="icon icon-devops"
                    ><use xlink:href="#icon-devops"></use></svg
                >
                <p>DevOps</p>
            </a>
            <a href="/services#v-pills-agentic">
                <svg class="icon icon-robot"
                    ><use xlink:href="#icon-robot"></use></svg
                >
                <p>Agentic</p>
            </a>
        </div>

        <div class="row">
            <div class="col-md-6 offset-md-3">
                <div class="d-grid gap-2 col-6 mx-auto">
                    <a
                        href="/contact"
                        class="btn btn-lg btn-custom"
                        type="button">get in touch</a
                    >
                </div>
            </div>
        </div>
    </div>
</section>
<!-- //Services -->

<!-- Water Ripple CTA -->
<section class="ripple-cta">
    <div class="water-container">
        <canvas id="pool" aria-hidden="true"></canvas>
    </div>

    <div class="viking-ship-container">
        <svg
            class="viking-ship"
            viewBox="0 0 200 100"
            xmlns="http://www.w3.org/2000/svg"
            aria-label="Viking ship sailing"
        >
            <!-- Ship Hull -->
            <path
                d="M 20 50 Q 10 60, 20 70 L 180 70 Q 190 60, 180 50 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="2"
            />

            <!-- Hull Details -->
            <rect
                x="25"
                y="55"
                width="150"
                height="3"
                fill="#3a2920"
                opacity="0.7"
            />
            <rect
                x="25"
                y="62"
                width="150"
                height="2"
                fill="#3a2920"
                opacity="0.5"
            />

            <!-- Mast -->
            <rect x="98" y="15" width="4" height="45" fill="#2a1f1a" />

            <!-- Sail -->
            <path
                d="M 45 20 Q 100 25, 155 20 L 150 45 Q 100 48, 50 45 Z"
                fill="#e8d5c4"
                stroke="#2a1f1a"
                stroke-width="1.5"
                opacity="0.95"
            />

            <!-- Sail Stripes -->
            <rect
                x="50"
                y="25"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="33"
                width="100"
                height="4"
                fill="#b84c4c"
                opacity="0.8"
            />
            <rect
                x="50"
                y="41"
                width="100"
                height="3"
                fill="#b84c4c"
                opacity="0.8"
            />

            <!-- Shield decorations -->
            <circle
                cx="40"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="60"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="80"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="100"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="120"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="140"
                cy="60"
                r="6"
                fill="#4a7299"
                stroke="#2a1f1a"
                stroke-width="1"
            />
            <circle
                cx="160"
                cy="60"
                r="6"
                fill="#b84c4c"
                stroke="#2a1f1a"
                stroke-width="1"
            />

            <!-- Dragon head -->
            <path
                d="M 180 50 Q 195 45, 197 50 L 195 55 Q 190 52, 180 52 Z"
                fill="#4a3426"
                stroke="#2a1f1a"
                stroke-width="1.5"
            />

            <!-- Oars (simplified) -->
            <line
                x1="35"
                y1="68"
                x2="28"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="55"
                y1="68"
                x2="48"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="75"
                y1="68"
                x2="68"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="125"
                y1="68"
                x2="132"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="145"
                y1="68"
                x2="152"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
            <line
                x1="165"
                y1="68"
                x2="172"
                y2="78"
                stroke="#2a1f1a"
                stroke-width="2"
                stroke-linecap="round"
            />
        </svg>
    </div>

    <div class="trees trees--cta" aria-hidden="true"></div>

    <div class="overlay" aria-hidden="true">
        <div class="overlay-inner">
            <h2 class="title">We take you from point A to point B.</h2>
            <h3 class="subtitle">And everywhere in-between.</h3>
            <div class="cta">
                Let's create custom web experiences for your customers!
            </div>
            <div class="logo-tag">
                <button
                    type="button"
                    class="btn btn-lg btn-custom"
                    data-fillout-id="n2vCxyr9Huus"
                    data-fillout-embed-type="popup"
                    data-fillout-dynamic-resize
                    data-fillout-inherit-parameters
                    data-fillout-popup-size="small">navigate the winds</button
                >
            </div>
        </div>
    </div>

    <!-- <div class="scroll-indicator">↓ scroll to sail ↓</div> -->

    <div class="fallback" hidden aria-hidden="true">
        WebGL2 is required to view this water effect.<br />
        Please try again in a modern browser.
    </div>
</section>
<!-- Water Ripple CTA -->
